/**
 * Offerte Cart Manager
 * Manages the quote basket using localStorage
 */
class OfferteCart {
  constructor() {
    this.storageKey = 'offerte_products';
    this.products = this.getProducts();

    this.itemsContainer = document.querySelector('[data-offerte-items]');
    this.emptyState = document.querySelector('[data-offerte-empty]');
    this.totalElement = document.querySelector('[data-offerte-total]');
    this.submitButton = document.querySelector('[data-offerte-submit]');

    // Form modal elements
    this.formModal = document.querySelector('[data-offerte-form-modal]');
    this.formSteps = document.querySelectorAll('[data-form-step]');
    this.currentStep = 1;
    this.formData = {};

    this.init();
  }

  init() {
    this.render();
    this.bindEvents();
    this.updateHeaderCount();
  }

  bindEvents() {
    // Submit button opens form modal
    if (this.submitButton) {
      this.submitButton.addEventListener('click', () => this.openFormModal());
    }

    // Form modal close buttons
    document.querySelectorAll('[data-offerte-form-close]').forEach(btn => {
      btn.addEventListener('click', () => this.closeFormModal());
    });

    // Form next step button
    const nextButton = document.querySelector('[data-form-next]');
    if (nextButton) {
      nextButton.addEventListener('click', () => this.nextStep());
    }

    // Form submission
    const form = document.querySelector('#offerte-contact-form');
    if (form) {
      form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    // Escape key closes modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.formModal?.hasAttribute('active')) {
        this.closeFormModal();
      }
    });
  }

  // localStorage methods
  getProducts() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error reading offerte products:', e);
      return [];
    }
  }

  saveProducts() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.products));
      this.updateHeaderCount();
    } catch (e) {
      console.error('Error saving offerte products:', e);
    }
  }

  addProduct(productData) {
    // Check if product already exists
    const exists = this.products.find(p => p.id === productData.id);
    if (!exists) {
      this.products.push(productData);
      this.saveProducts();
    }
    return !exists;
  }

  removeProduct(productId) {
    this.products = this.products.filter(p => p.id !== productId);
    this.saveProducts();
    this.render();
  }

  clearProducts() {
    this.products = [];
    this.saveProducts();
    this.render();
  }

  getTotal() {
    return this.products.reduce((sum, product) => {
      return sum + (parseInt(product.price) || 0);
    }, 0);
  }

  getTotalsByType() {
    return this.products.reduce(
      (totals, product) => {
        const price = parseInt(product.price) || 0;
        totals.totalCount += 1;

        if (product.perPersoon) {
          totals.perPersoonCount += 1;
          totals.perPersoon += price;
        } else {
          totals.normal += price;
        }

        return totals;
      },
      { normal: 0, perPersoon: 0, totalCount: 0, perPersoonCount: 0 }
    );
  }

  // Rendering
  render() {
    if (!this.itemsContainer) return;

    if (this.products.length === 0) {
      this.itemsContainer.innerHTML = '';
      this.emptyState?.classList.remove('hidden');
      this.submitButton?.setAttribute('disabled', 'disabled');
    } else {
      this.emptyState?.classList.add('hidden');
      this.submitButton?.removeAttribute('disabled');
      this.itemsContainer.innerHTML = this.products.map(product => this.renderProduct(product)).join('');

      // Bind remove buttons
      this.itemsContainer.querySelectorAll('[data-remove-product]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const productId = e.currentTarget.dataset.removeProduct;
          this.removeProduct(productId);
        });
      });
    }

    // Update total
    if (this.totalElement) {
      this.totalElement.innerHTML = this.formatSummaryTotal(this.getTotalsByType());
    }
  }

  renderProduct(product) {
    const priceDisplay = this.formatPrice(product.price, product.perPersoon);

    return `
      <div class="offerte-cart__item" data-product-id="${product.id}">
        <div class="offerte-cart__item-image">
          <img src="${product.image}" alt="${product.title}" loading="lazy">
        </div>
        <div class="offerte-cart__item-info">
          <h3 class="offerte-cart__item-title">${product.title}</h3>
          <button type="button" class="offerte-cart__item-remove" data-remove-product="${product.id}">
            Verwijderen
          </button>
        </div>
        <div class="offerte-cart__item-price">
          ${priceDisplay}
        </div>
      </div>
    `;
  }

  formatMoney(cents) {
    const amount = (parseInt(cents) / 100).toFixed(2);
    return `€${amount.replace('.', ',')}`;
  }

  formatPrice(cents, perPersoon = false) {
    const price = parseInt(cents);

    if (price === 0) {
      return 'Prijs op aanvraag';
    }

    const formattedMoney = this.formatMoney(cents);

    if (perPersoon) {
      return `Vanaf ${formattedMoney} p.p.`;
    }

    return `Prijs vanaf: ${formattedMoney}`;
  }

  formatSummaryTotal(totals) {
    const combined = totals.normal + totals.perPersoon;

    if (combined === 0) {
      return this.formatMoney(0);
    }

    if (totals.totalCount > 0 && totals.perPersoonCount === totals.totalCount) {
      return `Vanaf ${this.formatMoney(combined)} p.p.`;
    }

    return `Prijs vanaf: ${this.formatMoney(combined)}`;
  }

  // Header count update
  updateHeaderCount() {
    const countElements = document.querySelectorAll('[data-offerte-count]');
    const count = this.products.length;

    countElements.forEach(el => {
      el.textContent = count;
      if (count > 0) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });
  }

  // Form Modal methods
  openFormModal() {
    if (this.products.length === 0) return;

    // Populate hero sections with first product data
    this.populateHero();

    this.formModal?.setAttribute('open', '');
    this.formModal?.setAttribute('active', '');
    document.body.classList.add('overflow-hidden');
    this.goToStep(1);
  }

  populateHero() {
    if (this.products.length === 0) return;

    const firstProduct = this.products[0];
    const heroSections = this.formModal?.querySelectorAll('[data-hero-section]');

    heroSections?.forEach(hero => {
      // Set background image
      hero.style.backgroundImage = `url('${firstProduct.image}')`;

      // Set title
      const titleEl = hero.querySelector('[data-hero-title]');
      if (titleEl) {
        titleEl.textContent = firstProduct.title;
      }

      // Set price
      const priceEl = hero.querySelector('[data-hero-price]');
      if (priceEl) {
        priceEl.textContent = this.formatHeroPrice(firstProduct.price, firstProduct.perPersoon);
      }
    });
  }

  formatHeroPrice(cents, perPersoon = false) {
    const price = parseInt(cents);

    if (price === 0) {
      return 'Prijs op aanvraag';
    }

    const formattedPrice = this.formatMoney(cents);

    if (perPersoon) {
      return `Vanaf: ${formattedPrice} p.p.`;
    }

    return `Vanaf: ${formattedPrice}`;
  }

  closeFormModal() {
    this.formModal?.removeAttribute('open');
    this.formModal?.removeAttribute('active');
    document.body.classList.remove('overflow-hidden');

    // Reset form after close
    setTimeout(() => {
      this.resetForm();
    }, 300);
  }

  goToStep(step) {
    this.currentStep = step;

    this.formSteps.forEach(stepEl => {
      const stepNum = stepEl.dataset.formStep;
      if (stepNum === String(step) || stepNum === step) {
        stepEl.classList.remove('hidden');
      } else {
        stepEl.classList.add('hidden');
      }
    });
  }

  nextStep() {
    if (this.currentStep === 1) {
      if (!this.validateStep1()) {
        return;
      }
      this.saveStep1Data();
      this.goToStep(2);
    }
  }

  validateStep1() {
    const name = document.querySelector('[data-field="name"]')?.value;
    const email = document.querySelector('[data-field="email"]')?.value;

    if (!name || !email) {
      alert('Vul alstublieft alle verplichte velden in.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('Vul een geldig e-mailadres in.');
      return false;
    }

    return true;
  }

  saveStep1Data() {
    this.formData.name = document.querySelector('[data-field="name"]')?.value || '';
    this.formData.email = document.querySelector('[data-field="email"]')?.value || '';
    this.formData.phone = document.querySelector('[data-field="phone"]')?.value || '';
    this.formData.company = document.querySelector('[data-field="company"]')?.value || '';

    // Populate hidden fields
    const hiddenName = document.querySelector('[data-field="hidden_name"]');
    const hiddenEmail = document.querySelector('[data-field="hidden_email"]');
    const hiddenPhone = document.querySelector('[data-field="hidden_phone"]');
    const hiddenCompany = document.querySelector('[data-field="hidden_company"]');
    const productData = document.querySelector('[data-field="product_data"]');

    if (hiddenName) hiddenName.value = this.formData.name;
    if (hiddenEmail) hiddenEmail.value = this.formData.email;
    if (hiddenPhone) hiddenPhone.value = this.formData.phone;
    if (hiddenCompany) hiddenCompany.value = this.formData.company;
    if (productData) productData.value = JSON.stringify(this.products);
  }

  handleSubmit(e) {
    // Clear the cart after successful submission
    this.clearProducts();
    this.goToStep('success');

    // Prevent default form submission for now to show success
    // The form will still submit via Shopify's contact form
  }

  resetForm() {
    this.currentStep = 1;
    this.formData = {};

    // Reset input fields
    const inputs = this.formModal?.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], textarea');
    inputs?.forEach(input => input.value = '');

    // Reset to step 1
    this.goToStep(1);
  }
}

// Initialize cart on page load
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('[data-offerte-items]')) {
    window.offerteCart = new OfferteCart();
  }
});

// Global function to add product (called from offer modal)
window.addToOfferteCart = function(productData) {
  // Get or create cart instance
  let cart;
  if (window.offerteCart) {
    cart = window.offerteCart;
  } else {
    // Create a minimal cart just for adding
    const storageKey = 'offerte_products';
    let products = [];
    try {
      const data = localStorage.getItem(storageKey);
      products = data ? JSON.parse(data) : [];
    } catch (e) {
      products = [];
    }

    const exists = products.find(p => p.id === productData.id);
    if (!exists) {
      products.push(productData);
      localStorage.setItem(storageKey, JSON.stringify(products));
    }

    // Update header count
    const countElements = document.querySelectorAll('[data-offerte-count]');
    countElements.forEach(el => {
      el.textContent = products.length;
      if (products.length > 0) {
        el.classList.remove('hidden');
      }
    });

    return !exists;
  }

  const added = cart.addProduct(productData);
  return added;
};

// Get cart count (for header)
window.getOfferteCartCount = function() {
  try {
    const data = localStorage.getItem('offerte_products');
    const products = data ? JSON.parse(data) : [];
    return products.length;
  } catch (e) {
    return 0;
  }
};


